/**
 * components/Card/CardHeader
 * The `12px 15px` strip at the top of a card: title (13/600) with an optional
 * subtitle beneath, and a right slot for the one control the card offers
 * ("Worst first", "AED only", "Add rule", "See all").
 *
 * `filled` paints the #FAFAFA background the design uses on table cards; the
 * chart cards keep a white header with a #F2F2F2 hairline instead, which is
 * the `hairline` variant.
 */
import type { ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { card as cardSpace } from '../../theme/spacing';
import { type as textRole } from '../../theme/typography';

export interface CardHeaderProps {
  title: string;
  subtitle?: string;
  /** The single control on the right — a Button, a Chip row, a label. */
  right?: ReactNode;
  /** #FAFAFA fill with an #E4E4E4 rule — table cards. */
  filled?: boolean;
  /** White fill with a lighter #F2F2F2 rule — chart cards. */
  hairline?: boolean;
}

export function CardHeader({
  title,
  subtitle,
  right,
  filled = false,
  hairline = false,
}: CardHeaderProps) {
  return (
    <View
      style={[
        styles.header,
        filled && styles.filled,
        hairline && styles.hairline,
        !filled && !hairline && styles.plain,
      ]}
    >
      <View style={styles.titles}>
        <Text style={textRole.cardTitle} numberOfLines={1}>
          {title}
        </Text>
        {!!subtitle && (
          <Text style={textRole.cardSubtitle} numberOfLines={2}>
            {subtitle}
          </Text>
        )}
      </View>
      {!!right && <View style={styles.right}>{right}</View>}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: cardSpace.headerPaddingY,
    paddingHorizontal: cardSpace.headerPaddingX,
  },
  filled: {
    backgroundColor: neutral.surface2,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  hairline: {
    borderBottomWidth: 1,
    borderBottomColor: neutral.ruleLight,
  },
  plain: {},
  // `minWidth: 0` is what lets a long title ellipsise instead of pushing the
  // right-hand control off the card.
  titles: { flex: 1, minWidth: 0, gap: 2 },
  right: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
});

export default CardHeader;
